import { Component, Input, OnDestroy } from '@angular/core';
import { AuditMemberService } from 'src/app/core/service/audit-member.service';
import { TranslateService } from '@ngx-translate/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { ColumnSettings } from 'src/app/core/model/column-settings';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { PhonePipe } from 'src/app/shared/pipes/phone.pipe';
import { LanguageService } from 'src/app/core/service/language.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { Subscription } from 'rxjs';
import { MemberService } from 'src/app/core/service/member.service';

@Component({
  selector: 'app-audit-member-details',
  templateUrl: './audit-member-details.component.html',
  styleUrls: ['./audit-member-details.component.scss']
})
export class AuditMemberDetailsComponent implements OnDestroy
{
  public currentMemberId: number;
  public currentRevisionId: number;
  public currentAuditType: number; // 0 person demographics, 1 person extension
  public currentRevisionType: number; // 0 Create, 1 Update, 2 Delete
  public currentChangesOnly = false;
  public currentEnableAdvanced = false;

  public view: GridViewManager;
  public data = [];
  public pageSizes = [5, 10, 25, 50, 100];
  public count: number;
  public columnsConfig: ColumnSettings[];

  private messagingSubscription: Subscription;

  /**
   * Constructor
   */
  constructor(
    private auditMemberService: AuditMemberService,
    private translateService: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private languageService: LanguageService,
    private memberService: MemberService,
    private intl: IntlService)
  {
    this.auditMemberService = auditMemberService;
    this.translateService = translateService;
    this.messageHandlerService = messageHandlerService;

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'refreshAuditMemberDetails')
        {
          this.initGridColumnsAndLoadData();
        }
      }
    });
  }

  @Input() public set memberId(memberId: number)
  {
    if (memberId !== undefined)
    {
      this.currentMemberId = memberId;
    }
  }

  @Input() public set revisionId(revisionId: number)
  {
    if (revisionId !== undefined)
    {
      this.currentRevisionId = revisionId;

      if (this.view !== undefined)
      {
        this.view.startLoading();
      }

      this.view = new GridViewManager(99999);
      this.view.setData(this.data);

      this.initGridColumnsAndLoadData();
    }
  }

  @Input() public set enableAdvanced(enableAdvanced: boolean)
  {
    if (enableAdvanced !== undefined)
    {
      this.currentEnableAdvanced = enableAdvanced;
    }
  }

  @Input() public set changesOnly(changesOnly: boolean)
  {
    if (changesOnly !== undefined)
    {
      this.currentChangesOnly = changesOnly;
    }
  }

  @Input() public set revisionType(revisionType: number)
  {
    if (revisionType !== undefined)
    {
      this.currentRevisionType = revisionType;
    }
  }

  @Input() public set auditType(auditType: number)
  {
    if (auditType !== undefined)
    {
      this.currentAuditType = auditType;
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public getAuditMemberDetails(): void
  {
    this.auditMemberService.getAuditMemberDetails(this.currentMemberId, this.currentRevisionId).subscribe({
      next: (data) =>
      {
        this.count = data.length;
        this.data = [];
        let index = 0;
        data.forEach((d: any) =>
        {
          if (index === 0)
          {
            Object.keys(d).forEach(e =>
            {
              let propertyNameText = null;
              if (e !== 'id' && !e.includes('Id'))
              {
                if ((!this.currentChangesOnly) ||
                  ((this.currentChangesOnly && data.length === 2 && d[e] !== data[1][e])) || /* Update */
                  ((this.currentChangesOnly && data.length === 1)))                          /* Create */
                {
                  switch (e)
                  {
                    case 'givenName':
                      propertyNameText = this.translateService.instant('FIRST-NAME');
                      break;
                    case 'familyName':
                      propertyNameText = this.translateService.instant('LAST-NAME');
                      break;
                    case 'middleName':
                      propertyNameText = this.translateService.instant('MIDDLE-NAME');
                      break;
                    case 'preferredName':
                      propertyNameText = this.translateService.instant('PREFERRED-NAME');
                      break;
                    case 'namePronoun': // namePronounId
                      propertyNameText = this.translateService.instant('NAME-PRONOUN');
                      break;
                    case 'namePronounOther':
                      propertyNameText = this.translateService.instant('NAME-PRONOUN-OTHER');
                      break;
                    case 'dateOfBirth':
                      propertyNameText = this.translateService.instant('DATE-OF-BIRTH');
                      break;
                    case 'gender': // genderId
                      propertyNameText = this.translateService.instant('GENDER');
                      break;
                    case 'livesAlone':
                      propertyNameText = this.translateService.instant('LIVES-ALONE');
                      break;
                    case 'primaryCaregiver':
                      propertyNameText = this.translateService.instant('PRIMARY-CAREGIVER');
                      break;
                    case 'preferredVideoProvider': // preferredVideoProviderId
                      propertyNameText = this.translateService.instant('PREFERRED-VIDEO-PROVIDER');
                      break;
                    case 'englishProficiency': // englishProficiencyId
                      propertyNameText = this.translateService.instant('ENGLISH-PROFICIENCY');
                      break;
                    case 'interpreterNeed': // interpreterNeedId
                      propertyNameText = this.translateService.instant('INTERPRETER-NEED');
                      break;
                    case 'interpreterNeedOther':
                      propertyNameText = this.translateService.instant('INTERPRETER-NEED-OTHER');
                      break;
                    case 'homePhoneNumber':
                      propertyNameText = this.translateService.instant('HOME-PHONE');
                      break;
                    case 'homePhoneSafe':
                      propertyNameText = this.translateService.instant('HOME-PHONE-SAFE');
                      break;
                    case 'cellPhoneNumber':
                      propertyNameText = this.translateService.instant('CELL-PHONE');
                      break;
                    case 'cellPhoneSafe':
                      propertyNameText = this.translateService.instant('CELL-PHONE-SAFE');
                      break;
                    case 'workPhoneNumber':
                      propertyNameText = this.translateService.instant('WORK-PHONE-LABEL');
                      break;
                    case 'workPhoneSafe':
                      propertyNameText = this.translateService.instant('WORK-PHONE-SAFE');
                      break;
                    case 'workPhoneNumberExtension':
                      propertyNameText = this.translateService.instant('WORK-PHONE-LABEL') + ' ' + this.translateService.instant('PHONE-EXTENSION-PREFIX');
                      break;
                    case 'otherPhoneNumber':
                      propertyNameText = this.translateService.instant('OTHER-PHONE-LABEL');
                      break;
                    case 'otherPhoneSafe':
                      propertyNameText = this.translateService.instant('OTHER-PHONE-SAFE');
                      break;
                    case 'pagerNumber':
                      propertyNameText = this.translateService.instant('PAGER');
                      break;
                    case 'pagerSafe':
                      propertyNameText = this.translateService.instant('PAGER-SAFE');
                      break;
                    case 'faxNumber':
                      propertyNameText = this.translateService.instant('FAX-OR-OTHER');
                      break;
                    case 'faxSafe':
                      propertyNameText = this.translateService.instant('FAX-SAFE');
                      break;
                    case 'email':
                      propertyNameText = this.translateService.instant('EMAIL');
                      break;
                    case 'url':
                      propertyNameText = this.translateService.instant('URL');
                      break;
                    case 'preferredLanguage': // preferredLanguageId
                      propertyNameText = this.translateService.instant('NOTIFICATION-LANGUAGE');
                      break;
                    case 'notificationType': // notificationTypeId
                      propertyNameText = this.translateService.instant('NOTIFICATION');
                      break;
                    case 'maritalStatus': // maritalStatusId
                      propertyNameText = this.translateService.instant('MARITAL-STATUS');
                      break;
                    case 'veteranStatus': // veteranStatusId
                      propertyNameText = this.translateService.instant('VETERAN-STATUS');
                      break;
                    case 'religiousAffiliation': // religiousAffiliationId
                      propertyNameText = this.translateService.instant('RELIGIOUS-AFFILIATION');
                      break;
                    case 'sexualOrientation': // sexualOrientationId
                      propertyNameText = this.translateService.instant('SEXUAL-ORIENTATION');
                      break;
                    case 'employmentStatus': // employmentStatusId
                      propertyNameText = this.translateService.instant('EMPLOYMENT-STATUS');
                      break;
                    case 'familyIncome':
                      propertyNameText = this.translateService.instant('FAMILY-INCOME');
                      break;
                    case 'povertyLevel':
                      propertyNameText = this.translateService.instant('POVERTY-LEVEL');
                      break;
                    case 'educationLevel': // educationLevelId
                      propertyNameText = this.translateService.instant('EDUCATION-LEVEL');
                      break;
                    case 'householdStatus': // householdStatusId
                      propertyNameText = this.translateService.instant('HOUSING-STATUS');
                      break;
                    case 'householdSize':
                      propertyNameText = this.translateService.instant('HOUSEHOLD-SIZE');
                      break;
                    case 'dependents':
                      propertyNameText = this.translateService.instant('DEPENDENTS');
                      break;
                    case 'preferredContactMethods':
                      propertyNameText = this.translateService.instant('PREFERRED-CONTACT-METHODS');
                      break;
                    case 'tribalAffiliated':
                      propertyNameText = this.translateService.instant('TRIBAL-AFFILIATED');
                      break;
                    case 'tribalEnrolled':
                      propertyNameText = this.translateService.instant('TRIBAL-ENROLLED');
                      break;
                    default:
                      propertyNameText = e;
                      break;
                  }
                  let newValueText = null;
                  let previousValueText = null;
                  switch (e)
                  {
                    case 'homePhoneNumber':
                    case 'cellPhoneNumber':
                    case 'workPhoneNumber':
                    case 'otherPhoneNumber':
                    case 'pagerNumber':
                    case 'faxNumber':
                      {
                        newValueText = d[e] ? new PhonePipe(this.languageService).transform(d[e]) : '';
                        if (data.length === 2)
                        {
                          previousValueText = data[1][e] ? new PhonePipe(this.languageService).transform(data[1][e]) : '';
                        }
                        break;
                      }
                    case 'homePhoneSafe':
                    case 'cellPhoneSafe':
                    case 'workPhoneSafe':
                    case 'otherPhoneSafe':
                    case 'pagerSafe':
                    case 'faxSafe':
                    case 'livesAlone':
                    case 'primaryCaregiver':
                    case 'tribalAffiliated':
                    case 'tribalEnrolled':
                      {
                        newValueText = d[e] ? this.translateService.instant('YES') : this.translateService.instant('NO');
                        if (data.length === 2)
                        {
                          previousValueText = data[1][e] ? this.translateService.instant('YES') : this.translateService.instant('NO');
                        }
                        break;
                      }
                    case 'preferredContactMethods':
                      newValueText = d[e] ? this.memberService.convertPreferredContactMethodsToDisplayTexts(d[e]) : '';
                      if (data.length === 2)
                      {
                        previousValueText = data[1][e] ? this.memberService.convertPreferredContactMethodsToDisplayTexts(data[1][e]) : '';
                      }
                      break;
                    case 'notificationType':
                      {
                        switch (d[e])
                        {
                          case 'EMAIL':
                            newValueText = this.translateService.instant('EMAIL');
                            break;
                          case 'SMS':
                            newValueText = this.translateService.instant('SMS');
                            break;
                          case 'NO_NOTIFICATION':
                            newValueText = this.translateService.instant('NO_NOTIFICATION');
                            break;
                        }
                        if (data.length === 2)
                        {
                          switch (data[1][e])
                          {
                            case 'EMAIL':
                              previousValueText = this.translateService.instant('EMAIL');
                              break;
                            case 'SMS':
                              previousValueText = this.translateService.instant('SMS');
                              break;
                            case 'NO_NOTIFICATION':
                              previousValueText = this.translateService.instant('NO_NOTIFICATION');
                              break;
                          }
                        }
                        break;
                      }
                    case 'dateOfBirth':
                      {
                        newValueText = d[e] ? this.intl.formatDate(this.intl.parseDate(d[e]), 'd') : '';
                        if (data.length === 2)
                        {
                          previousValueText = data[1][e] ? this.intl.formatDate(this.intl.parseDate(data[1][e]), 'd') : '';
                        }
                        break;
                      }
                  }
                  this.data.push({
                    propertyName: propertyNameText,
                    newValue: newValueText !== null ? newValueText : d[e],
                    previousValue: previousValueText !== null ? (data.length === 2 ? previousValueText : '') : (data[1] ? data[1][e] : '')
                  });
                }
              }
            });
          }
          index++;
        });
        this.view.setData(this.data);
        this.view.setSort([{ field: 'propertyName', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  public getAuditMemberExtensionDetails(): void
  {
    this.auditMemberService.getAuditMemberExtensionDetails(this.currentMemberId, this.currentRevisionId, this.currentRevisionType).subscribe({
      next: (data) =>
      {
        this.count = data.length;
        this.data = [];
        const processedIds = [];
        data.forEach((d: any) =>
        {
          if (processedIds.findIndex(x => x === d.id) === -1)
          {
            const patientExtension = data.filter(e => e.id === d.id);
            if (patientExtension.length === 1) // Create or Delete
            {
              // for Create and Delete do not show extensions created in previous revisions
              if (!(((this.currentRevisionType === 0) || (this.currentRevisionType === 2)) && (this.currentRevisionId !== d.revisionId)))
              {
                const l1 = {
                  sourceOrganization: d.organizationName,
                  propertyName: d.settingExtensionName,
                  newValue: d.value,
                  previousValue: ''
                }
                this.data.push(l1);
              }
            }
            else
              if (patientExtension.length === 2) // Update
              {
                const l2 = {
                  sourceOrganization: d.organizationName,
                  previousSourceOrganization: patientExtension[1].organizationName,
                  propertyName: d.settingExtensionName,
                  previousPropertyName: patientExtension[1].settingExtensionName,
                  newValue: patientExtension[0].value,
                  previousValue: patientExtension[1].value,
                }
                this.data.push(l2);
              }
            processedIds.push(d.id);
          }
        });
        this.view.setData(this.data);
        this.view.setSort([{ field: 'propertyName', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  private initGridColumns(res: any): void
  {
    // currentAuditType: 0 person demographics, 1 person extension
    // currentRevisionType: 0 Create, 1 Update, 2 Delete
    this.columnsConfig = [
      {
        field: 'sourceOrganization',
        title: (this.currentRevisionType === 0) || (this.currentRevisionType === 2) ? res['SOURCE-ORGANIZATION'] : res['NEW-SOURCE-ORGANIZATION'],
        titleKeys: (this.currentRevisionType === 0) || (this.currentRevisionType === 2) ? res['SOURCE-ORGANIZATION'] : res['NEW-SOURCE-ORGANIZATION'],
        width: 20, sortable: true, hidden: this.currentAuditType === 0, filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'propertyName',
        title: (this.currentAuditType === 0) ? res['PROPERTY-NAME'] : (this.currentRevisionType === 0) || (this.currentRevisionType === 2) ? res.NAME : res['NEW-NAME'],
        titleKeys: (this.currentAuditType === 0) ? res['PROPERTY-NAME'] : (this.currentRevisionType === 0) || (this.currentRevisionType === 2) ? res.NAME : res['NEW-NAME'],
        width: 20, sortable: true, hidden: false, filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'newValue',
        title: this.currentAuditType === 1 && ((this.currentRevisionType === 0) || (this.currentRevisionType === 2)) ? res.VALUE : res['NEW-VALUE'],
        titleKeys: this.currentAuditType === 1 && ((this.currentRevisionType === 0) || (this.currentRevisionType === 2)) ? res.VALUE : res['NEW-VALUE'],
        width: 40, sortable: true, hidden: false, filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'previousSourceOrganization', title: res['PREVIOUS-SOURCE-ORGANIZATION'], titleKeys: res['PREVIOUS-SOURCE-ORGANIZATION'],
        width: 20, sortable: true, hidden: (this.currentAuditType === 0) || ((this.currentAuditType === 1) && (this.currentRevisionType !== 1)), filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'previousPropertyName', title: res['PREVIOUS-NAME'], titleKeys: res['PREVIOUS-NAME'],
        width: 20, sortable: true, hidden: (this.currentAuditType === 0) || ((this.currentAuditType === 1) && (this.currentRevisionType !== 1)), filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'previousValue', title: res['PREVIOUS-VALUE'], titleKeys: ['PREVIOUS-VALUE'],
        width: 40, sortable: true, hidden: this.currentRevisionType === 0 || ((this.currentAuditType === 1) && (this.currentRevisionType === 2)), filterable: this.currentEnableAdvanced, dataType: 'string'
      }
    ];
  }

  private initGridColumnsAndLoadData(): void
  {
    this.translateService.get(['PROPERTY-NAME', 'NEW-VALUE', 'PREVIOUS-VALUE', 'VALUE', 'NAME',
      'SOURCE-ORGANIZATION', 'NEW-SOURCE-ORGANIZATION', 'PREVIOUS-SOURCE-ORGANIZATION', 'NEW-NAME', 'PREVIOUS-NAME']).subscribe({
        next: (res) =>
        {
          this.initGridColumns(res);
          if (this.currentAuditType === 0)
          {
            this.getAuditMemberDetails();
          }
          else
            if (this.currentAuditType === 1)
            {
              this.getAuditMemberExtensionDetails();
            }
        }
      });
  }
}
